import React, { useEffect, useRef, useState } from 'react';
import { recordEvent } from '../../utils/analytics';
import { blogShareLinks, blogShareUrl } from '../../utils/blogShare';
import { copyToClipboard } from '../../utils/sharingUrls';

type BlogMetaShareProps = {
  slug: string;
  title: string;
};

function preferNativeShare() {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(max-width: 720px), (pointer: coarse)').matches;
}

const BlogMetaShare: React.FC<BlogMetaShareProps> = ({ slug, title }) => {
  const links = blogShareLinks({ slug, title });
  const rootRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [copyLabel, setCopyLabel] = useState('Copy link');
  const resetTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const track = (method: string) => {
    recordEvent('share', { method, source: 'blog_post', slug });
  };

  const showNotice = (message: string, label: string) => {
    setNotice(message);
    setCopyLabel(label);
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => {
      setNotice('');
      setCopyLabel('Copy link');
      resetTimer.current = null;
    }, 2000);
  };

  const shareNative = () => {
    const url = blogShareUrl(slug, 'native');
    navigator.share({ title, text: title, url }).then(() => {
      track('native');
    }).catch((error: { name?: string }) => {
      if (error?.name === 'AbortError') return;
      setOpen(true);
    });
  };

  const onShareClick = () => {
    if (preferNativeShare()) {
      shareNative();
      return;
    }
    setOpen((value) => !value);
  };

  const copyLink = () => {
    copyToClipboard(links.copy).then((ok) => {
      if (!ok) return;
      showNotice('Link copied', 'Link copied');
      track('copy');
    });
  };

  const openInstagram = (url: string, message: string, method: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const href = event.currentTarget.href;
    copyToClipboard(url).then((ok) => {
      if (ok) showNotice(message, 'Copy link');
      track(method);
      window.open(href, '_blank', 'noopener,noreferrer');
    });
  };

  return (
    <span className="blog-meta-share" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="blog-meta-share-btn"
        aria-label="Share this post"
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls="blog-meta-share-menu"
        onClick={onShareClick}
      >
        <i className="fas fa-share-alt" aria-hidden="true" />
        <span>Share</span>
      </button>
      {open ? (
        <span id="blog-meta-share-menu" className="blog-meta-share-menu">
          <a
            className="blog-share-whatsapp"
            href={links.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => { track('whatsapp'); setOpen(false); }}
          >
            <i className="fab fa-whatsapp" aria-hidden="true" />
            <span>WhatsApp</span>
          </a>
          <a
            className="blog-share-instagram-post"
            href={links.instagramPost}
            target="_blank"
            rel="noopener noreferrer"
            data-share-url={links.instagramPostText}
            onClick={openInstagram(links.instagramPostText, 'Link copied — paste it into your post', 'instagram_post')}
          >
            <i className="fab fa-instagram" aria-hidden="true" />
            <span>Insta post</span>
          </a>
          <a
            className="blog-share-instagram-story"
            href={links.instagramStory}
            target="_blank"
            rel="noopener noreferrer"
            data-share-url={links.instagramStoryText}
            onClick={openInstagram(links.instagramStoryText, 'Link copied — paste it into your story', 'instagram_story')}
          >
            <i className="fab fa-instagram" aria-hidden="true" />
            <span>Insta story</span>
          </a>
          <a
            className="blog-share-facebook"
            href={links.facebook}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Facebook"
            onClick={() => { track('facebook'); setOpen(false); }}
          >
            <i className="fab fa-facebook-f" aria-hidden="true" />
            <span>Facebook</span>
          </a>
          <a
            className="blog-share-email"
            href={links.email}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => { track('email'); setOpen(false); }}
          >
            <i className="fas fa-envelope" aria-hidden="true" />
            <span>Email</span>
          </a>
          <a
            className="blog-share-linkedin"
            href={links.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => { track('linkedin'); setOpen(false); }}
          >
            <i className="fab fa-linkedin-in" aria-hidden="true" />
            <span>LinkedIn</span>
          </a>
          <button type="button" className="blog-share-copy" data-share-url={links.copy} onClick={copyLink}>
            <i className="fas fa-link" aria-hidden="true" />
            <span>{copyLabel}</span>
          </button>
        </span>
      ) : null}
      <span className="blog-meta-share-status" role="status" aria-live="polite">{notice}</span>
    </span>
  );
};

export default BlogMetaShare;
